import PatientSignatureImage from './PatientSignatureImage';

const CataractForm = ({
    pageRefs,
    currentPage,
    logo,
    pageTitles,
    SurgeryFormData,
    surgeonOptionsMap,
    docSignature,
    setSignatures,
    setSurgeryFormData,
    handleInputChange,
    invalidFields,
    isNext,
    isEditing,
    signatures,
    canvasRefs,
    updateSignatureData,
    clearSignature,
    toggleEditMode,
    getCentralAmericaDate,
    getCentralAmericaTime,
    Logo,
    Head,
    Paragraph,
    RadioGroup,
    TextInput,
    SignatureField,
    SegLogoBanner,
    Initials,
    surgeryUpload,
    FormInput,
    formattedDate,
    handleEyeChange,
    MultiSelector,
    MultiSelectorWithoutEyes,
    MultiFieldSelector,
    handleChange,
    eye,
    CheckboxField,
    SegFooter
}) => {

    const handleLocalInputChange = (e, fieldKey) => {
        const { value } = e.target;
        const [parent, child] = fieldKey.split(".");

        setSurgeryFormData((prev) => ({
            ...prev,
            [parent]: {
                ...prev[parent],
                [child]: value,
            },
        }));
    };

    return (
        <div ref={pageRefs.current[currentPage]} className="max-w-5xl w-full bg-white">
            <SegLogoBanner src={logo} />

            <div className="px-1 mx-8 py-2">
                <Paragraph>
                    Patient Name: <span className="underline font-bold">{SurgeryFormData.first_name} {SurgeryFormData.last_name}</span>  DOB: <span className="underline font-bold">{SurgeryFormData.dob}</span>
                </Paragraph>
                <div className="flex items-center justify-center w-full">
                    <Paragraph className="text-center">
                        <span className={eye === "os" ? "line-through font-bold" : "font-bold"}>
                            RIGHT EYE
                        </span>
                        /
                        <span className={eye === "od" ? "line-through font-bold" : "font-bold"}>
                            LEFT EYE
                        </span>
                    </Paragraph>
                </div>


                <CheckboxField
                    id="monofocal"
                    label="Monofocal IOL/Glasses"
                    checked={SurgeryFormData?.[`cataract_${eye}`]?.monofocal || false}
                    onChange={(e) => handleEyeChange(e, eye)}
                    disabled={isNext || surgeryUpload}
                />
                <Paragraph>
                    I wish to have a cataract operation with a monofocal IOL implant. I understand that glasses may be required for both distance and near to optimize my vision. Targeting:{" "}
                    <input
                        className="bg-slate-50 border-0 border-b-2 border-b-gray-300"
                        type="text"
                        value={SurgeryFormData?.[`cataract_${eye}`]?.targeting || ""}
                        onChange={(e) => handleLocalInputChange(e, `cataract_${eye}.targeting`)}
                        disabled={isNext || surgeryUpload}
                    />
                </Paragraph>

                <CheckboxField
                    id="monovision"
                    label="Monovision (patient understands they may still need glasses)"
                    checked={SurgeryFormData?.[`cataract_${eye}`]?.monovision || false}
                    onChange={(e) => handleEyeChange(e, eye)}
                    disabled={isNext || surgeryUpload}
                />
                <Paragraph>I wish to have a cataract operation with two different-powered IOLs implanted to achieve monovision.</Paragraph>
                <Paragraph>
                    I wish to have the above selected eye corrected for
                    <input
                        className="bg-slate-50 border-0 border-b-2 border-b-gray-300"
                        type="text"
                        value={SurgeryFormData?.[`cataract_${eye}`]?.near_or_distance_vision || ""}
                        onChange={(e) => handleLocalInputChange(e, `cataract_${eye}.near_or_distance_vision`)}
                        disabled={isNext || surgeryUpload}
                    />
                </Paragraph>

                <CheckboxField
                    id="multifocal"
                    label="Multifocal IOL (may still need glasses)"
                    checked={SurgeryFormData?.[`cataract_${eye}`]?.multifocal || false}
                    onChange={(e) => handleEyeChange(e, eye)}
                    disabled={isNext || surgeryUpload}
                />
                <Paragraph>
                    I wish to have a cataract operation with a multifocal IOL implant.
                    <input
                        className="bg-slate-50 border-0 border-b-2 border-b-gray-300"
                        type="text"
                        value={SurgeryFormData?.[`cataract_${eye}`]?.number_of_implant || ""}
                        onChange={(e) => handleLocalInputChange(e, `cataract_${eye}.number_of_implant`)}
                        disabled={isNext || surgeryUpload}
                        placeholder="No Of Implants"
                    />
                </Paragraph>
                <br />

                <Paragraph>
                    The multifocal IOL is a premium IOL and has an extra charge of $
                    <input
                        className="bg-slate-50 border-0 border-b-2 border-b-gray-300"
                        type="text"
                        value={SurgeryFormData?.[`cataract_${eye}`]?.cost || ""}
                        onChange={(e) => handleLocalInputChange(e, `cataract_${eye}.cost`)}
                        disabled={isNext || surgeryUpload}
                    />
                </Paragraph>

                <CheckboxField
                    id="toric_monofocal"
                    label="Toric Monofocal IOL/Astigmatism Managing"
                    checked={SurgeryFormData?.[`cataract_${eye}`]?.toric_monofocal || false}
                    onChange={(e) => handleEyeChange(e, eye)}
                    disabled={isNext || surgeryUpload}
                />
                <Paragraph>
                    I wish to have a cataract operation with a Toric Monofocal IOL and wear glasses for vision.
                    <input
                        className="bg-slate-50 border-0 border-b-2 border-b-gray-300"
                        type="text"
                        value={SurgeryFormData?.[`cataract_${eye}`]?.toric_distance || ""}
                        onChange={(e) => handleLocalInputChange(e, `cataract_${eye}.toric_distance`)}
                        disabled={isNext || surgeryUpload}
                        placeholder="Near Or Distance Vision"
                    />
                </Paragraph>
                <Paragraph>
                    The Toric IOL is a premium IOL and has an extra charge of $
                    <input
                        className="bg-slate-50 border-0 border-b-2 border-b-gray-300"
                        type="text"
                        value={SurgeryFormData?.[`cataract_${eye}`]?.toric_cost || ""}
                        onChange={(e) => handleLocalInputChange(e, `cataract_${eye}.toric_cost`)}
                        disabled={isNext || surgeryUpload}
                    />
                </Paragraph>

                <CheckboxField
                    id="lri"
                    label="Limbal Relaxing Incision for Astigmatism Management"
                    checked={SurgeryFormData?.[`cataract_${eye}`]?.lri || false}
                    onChange={(e) => handleEyeChange(e, eye)}
                    disabled={isNext || surgeryUpload}
                />
                <Paragraph>
                    I wish to have this procedure done in addition to the cataract operation to reduce the amount of
                    astigmatism.
                </Paragraph>
                <Paragraph>
                    The Limbal Relaxing Incision is a non-covered procedure and has an extra charge of $
                    <input
                        className="bg-slate-50 border-0 border-b-2 border-b-gray-300"
                        type="text"
                        value={SurgeryFormData?.[`cataract_${eye}`]?.lri_cost || ""}
                        onChange={(e) => handleLocalInputChange(e, `cataract_${eye}.lri_cost`)}
                        disabled={isNext || surgeryUpload}
                    />
                </Paragraph>

                <CheckboxField
                    id="lal"
                    label="Light Adjustable LensTM (LAL®)"
                    checked={SurgeryFormData?.[`cataract_${eye}`]?.lal || false}
                    onChange={(e) => handleEyeChange(e, eye)}
                    disabled={isNext || surgeryUpload}
                />
                <Paragraph>
                    I wish to have a cataract operation with placement of the LAL implant.
                </Paragraph>
                <Paragraph>
                    The LAL is a premium IOL and has an extra charge of $
                    <input
                        className="bg-slate-50 border-0 border-b-2 border-b-gray-300"
                        type="text"
                        value={SurgeryFormData?.[`cataract_${eye}`]?.lal_cost || ""}
                        onChange={(e) => handleLocalInputChange(e, `cataract_${eye}.lal_cost`)}
                        disabled={isNext || surgeryUpload}
                    />
                </Paragraph>
                <div className="mb-4">
                    <PatientSignatureImage
                        signature={SurgeryFormData['patient_signature']}
                        label="Patient Signature"
                        signedDate={`${SurgeryFormData.patientSignedDate} ${SurgeryFormData.patientSignedTime}`}
                    />
                </div>


                <div className="mb-4 flex flex-col sm:flex-row sm:items-start gap-6">
                    {/* Signature */}
                    <PatientSignatureImage
                        signature={SurgeryFormData['doctor_signature']}
                        label="Physician/Surgeon"
                        signedDate={formattedDate}
                    />
                </div>
            </div>
            <div className="mb-2">
                <SegFooter />
            </div>
        </div >
    );
};

export default CataractForm;